import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  loadMe,
  login as loginRequest,
  logout as logoutRequest,
  readStoredSession,
} from '../repositories/auth.repository';
import { TOKEN_KEYS } from '../config/api';

const AuthContext = createContext(null);

function readInitialState() {
  const session = readStoredSession();
  const legacy = localStorage.getItem('utkarsh_admin_token');
  const accessToken = session.accessToken || legacy || null;
  if (accessToken && !session.accessToken && legacy) {
    localStorage.setItem(TOKEN_KEYS.access, legacy);
  }
  return {
    accessToken,
    refreshToken: session.refreshToken || null,
    staff: session.staff,
    permissions: session.permissions || {},
    isSuperAdmin: Boolean(session.isSuperAdmin),
  };
}

export function AuthProvider({ children }) {
  const initial = readInitialState();
  const [accessToken, setAccessToken] = useState(initial.accessToken);
  const [refreshToken, setRefreshToken] = useState(initial.refreshToken);
  const [staff, setStaff] = useState(initial.staff);
  const [permissions, setPermissions] = useState(initial.permissions);
  const [isSuperAdmin, setIsSuperAdmin] = useState(initial.isSuperAdmin);
  const [loading, setLoading] = useState(Boolean(initial.accessToken));

  const applySession = useCallback((session) => {
    setAccessToken(session.accessToken || null);
    setRefreshToken(session.refreshToken || null);
    setStaff(session.staff || null);
    setPermissions(session.permissions || {});
    setIsSuperAdmin(Boolean(session.isSuperAdmin));
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setStaff(null);
    setPermissions({});
    setIsSuperAdmin(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      if (!accessToken) {
        setLoading(false);
        return;
      }
      try {
        const me = await loadMe();
        if (cancelled) return;
        setStaff(me.staff);
        setPermissions(me.permissions || {});
        setIsSuperAdmin(Boolean(me.isSuperAdmin));
      } catch {
        if (cancelled) return;
        clearSession();
        localStorage.removeItem(TOKEN_KEYS.access);
        localStorage.removeItem(TOKEN_KEYS.refresh);
        localStorage.removeItem(TOKEN_KEYS.staff);
        localStorage.removeItem(TOKEN_KEYS.permissions);
        localStorage.removeItem(TOKEN_KEYS.isSuperAdmin);
        localStorage.removeItem('utkarsh_admin_token');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    hydrate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount when token exists
  }, []);

  const login = useCallback(
    async (email, password) => {
      const session = await loginRequest(email, password);
      applySession(session);
      return session;
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    await logoutRequest();
    clearSession();
  }, [clearSession]);

  const hasPermission = useCallback(
    (module, action) => {
      if (isSuperAdmin) return true;
      return Boolean(permissions?.[module]?.[action]);
    },
    [isSuperAdmin, permissions],
  );

  const value = useMemo(
    () => ({
      accessToken,
      refreshToken,
      staff,
      permissions,
      isSuperAdmin,
      loading,
      isAuthenticated: Boolean(accessToken),
      login,
      logout,
      hasPermission,
      loadMe: async () => {
        const me = await loadMe();
        setStaff(me.staff);
        setPermissions(me.permissions || {});
        setIsSuperAdmin(Boolean(me.isSuperAdmin));
        return me;
      },
    }),
    [
      accessToken,
      refreshToken,
      staff,
      permissions,
      isSuperAdmin,
      loading,
      login,
      logout,
      hasPermission,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
